import classnames from 'clsx';
import { createSelector } from 'reselect';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { noop } from 'ui-components/default-props/noop';

import style from './simple-dropdown.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import { useUniqueHtmlId } from '../../hooks/use-unique-html-id';

const elementId = 'simple-dropdown';

const getOnItemSelected = createSelector(
  (onItemSelected) => onItemSelected,
  (_, key) => key,
  (onItemSelected, key) => () => onItemSelected(key),
);

/**
 * @typedef {import('react').ReactNode} Node
 */

/**
 * @param {object} props
 * @param {Node} [props.defaultTitle]
 * @param {Node} [props.label]
 * @param {Array<{key: string, name: Node, disabled?: boolean}>} props.items
 * @param {boolean} [props.dropdownMenuShouldHaveWidthOfButton]
 * @param {(selected: string) => void} [props.onItemSelected]
 * @param {boolean} [props.required]
 * @param {boolean} [props.disabled]
 * @param {string} [props.selectedItemKey]
 * @param {boolean} [props.hasError]
 * @param {'button'|'form'} [props.variant]
 * @param {string} [props.theme]
 * @param {boolean} [props.fullWidth]
 * @returns {React.JSX.Element}
 */
export const SimpleDropdown = ({
  defaultTitle,
  dropdownMenuShouldHaveWidthOfButton = false,
  items = emptyArray,
  label,
  onItemSelected = noop,
  required = false,
  selectedItemKey = '',
  hasError = false,
  variant = 'form',
  theme = '',
  fullWidth = false,
  disabled = false,
}) => {
  const selectedItem = selectedItemKey && items.length > 0 && items.find((item) => item.key === selectedItemKey);
  const title = selectedItem ? selectedItem.name : defaultTitle;
  const id = useUniqueHtmlId(elementId);

  return (
    <div
      className={classnames(style['simple-dropdown'], {
        [style['simple-dropdown-without-label']]: !label,
        [style['simple-dropdown-full-width']]: fullWidth,
      })}
    >
      {!!label && (
        <label className={style['simple-dropdown-label']} htmlFor={id}>
          {label}
        </label>
      )}
      <DropdownMenu
        className={style['simple-dropdown-menu']}
        disabled={items.length === 0 || disabled}
        dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
        fullWidth={fullWidth}
        hasError={hasError}
        htmlFor={id}
        variant={variant}
      >
        <DropdownMenuToggler hasError={hasError} required={required} theme={theme}>
          {title}
        </DropdownMenuToggler>
        {items.map(({ key, name, disabled: itemDisabled }) => (
          <DropdownMenuItem
            key={key}
            disabled={itemDisabled}
            onClick={getOnItemSelected(onItemSelected, key)}
            selected={key === selectedItemKey}
          >
            {name}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};
